<template>
  <div id="map-container" class="relative flex-grow h-full">
    <div id="map" class="relative w-full h-full z-10 bg-gray-200"></div>
    <LoadingModal v-if="loading" :message="loadingModal.message" :val="resultDownload.current" :max="resultDownload.max" />
    <div class="absolute top-0 right-0 h-full overflow-y-scroll">
      <div class="m-3 z-20 flex items-end justify-center flex-col">
        <MapLegend v-if="!loading" :scale="colorScale" :mainTitle=metadata.label :variable="variable" />
        <DetailPanel v-if="!loading" :zone=detailPanelContent />
      </div>
    </div>
  </div>
</template>

<script>
import * as L from 'leaflet';
import 'leaflet.vectorgrid';
import axios from 'axios';
import * as d3Scale from 'd3-scale';
import scaleCluster from 'd3-scale-cluster';
import * as d3Color from 'd3-scale-chromatic';

import 'leaflet/dist/leaflet.css';

import { EventBus } from '../event-bus';

import LoadingModal from './LoadingModal.vue';
import MapLegend from './MapLegend.vue';
import DetailPanel from './DetailPanel.vue';

const colorOptions = {
  blue: {
    scheme: d3Color.schemeBlues,
    interpolator: d3Color.interpolateBlues,
  },
  green: {
    scheme: d3Color.schemeGreens,
    interpolator: d3Color.interpolateGreens,
  },
  grey: {
    scheme: d3Color.schemeGreys,
    interpolator: d3Color.interpolateGreys,
  },
  orange: {
    scheme: d3Color.schemeOranges,
    interpolator: d3Color.interpolateOranges,
  },
  purple: {
    scheme: d3Color.schemePurples,
    interpolator: d3Color.interpolatePurples,
  },
  red: {
    scheme: d3Color.schemeReds,
    interpolator: d3Color.interpolateReds,
  },
  'blue-green': {
    scheme: d3Color.schemeBuGn,
    interpolator: d3Color.interpolateBuGn,
  },
  'blue-purple': {
    scheme: d3Color.schemeBuPu,
    interpolator: d3Color.interpolateBuPu,
  },
  'green-blue': {
    scheme: d3Color.schemeGnBu,
    interpolator: d3Color.interpolateGnBu,
  },
  'orange-red': {
    scheme: d3Color.schemeOrRd,
    interpolator: d3Color.interpolateOrRd,
  },
  'purple-blue-green': {
    scheme: d3Color.schemePuBuGn,
    interpolator: d3Color.interpolatePuBuGn,
  },
  'purple-blue': {
    scheme: d3Color.schemePuBu,
    interpolator: d3Color.interpolatePuBu,
  },
  'purple-red': {
    scheme: d3Color.schemePuRd,
    interpolator: d3Color.interpolatePuRd,
  },
  'red-purple': {
    scheme: d3Color.schemeRdPu,
    interpolator: d3Color.interpolateRdPu,
  },
  'yellow-green-blue': {
    scheme: d3Color.schemeYlGnBu,
    interpolator: d3Color.interpolateYlGnBu,
  },
  'yellow-green': {
    scheme: d3Color.schemeYlGn,
    interpolator: d3Color.interpolateYlGn,
  },
  'yellow-orange-brown': {
    scheme: d3Color.schemeYlOrBr,
    interpolator: d3Color.interpolateYlOrBr,
  },
  'yellow-orange-red': {
    scheme: d3Color.schemeYlOrRd,
    interpolator: d3Color.interpolateYlOrRd,
  },
};

function getColorRange(colorScheme, nColorCategories) {
  if (nColorCategories < 10) {
    return colorOptions[colorScheme].scheme[nColorCategories];
  }
  const scale = d3Scale.scaleSequential()
    .domain([1, 10])
    .interpolator(colorOptions[colorScheme].interpolator);
  const range = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => scale(n));
  return range;
}

function initMap() {
  this.map = L.map('map', {
    renderer: L.canvas(),
    minZoom: 2,
    maxZoom: 16,
    maxBounds: [[-90, -180], [90, 180]],
    maxBoundsViscosity: 1,
  }).setView([52, 20], 4);
  L.control.scale().addTo(this.map);
  //  this.tileLayer = L.tileLayer('http://localhost:8081/styles/klokantech-basic/{z}/{x}/{y}.png');
  //  this.tileLayer = L.tileLayer('http://{s}.tile.osm.org/{z}/{x}/{y}.png');
  //  this.tileLayer = L.tileLayer('http://a.tile.stamen.com/toner/{z}/{x}/{y}.png');
  //  this.tileLayer = L.tileLayer('https://cartodb-basemaps-{s}.global.ssl.fastly.net/rastertiles/voyager/{z}/{x}/{y}.png');
  //  this.tileLayer = L.tileLayer('https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png');
  //  this.tileLayer = L.tileLayer('https://maps.wikimedia.org/osm-intl/{z}/{x}/{y}.png');
  //  this.tileLayer = L.tileLayer('http://vre.hcmut.edu.vn/styles/klokantech-basic/{z}/{x}/{y}.png');
  this.tileLayer = L.tileLayer('https://g3053r83r.cortext.net/styles/custom/{z}/{x}/{y}.png', { noWrap: true });
  this.tileLayer.addTo(this.map);
  this.map.attributionControl.addAttribution('&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | &copy; <a href="https://carto.com/attribution">CARTO</a> | <a href="https://www.cortext.net/">CorText</a>');
  this.selectedLevel = this.$route.params.level;
}

function changeScaleType(newScaleType) {
  this.scales.selected = newScaleType;
  this.buildScale();
}

function buildScale() {
  const densities = this.geoJson.features.map(
    element => this.calculateValueOfInterest(element.properties),
  );
  // this.colorScale = d3Scale.scaleQuantile()
  const range = this.getColorRange(this.colorScheme, this.nColorCategories);
  this.colorScale = this.scales.options[this.scales.selected]()
    .domain(densities)
    .range(range);
}

function styleFunction() {
  const defaultStyle = this.defaultFeatureStyle;
  const colorFn = this.fillColor;
  return function style(feature) {
    return {
      ...defaultStyle,
      fillColor: colorFn(this.calculateValueOfInterest(feature.properties)),
    };
  };
}

function vectorTileLayerStyleFunction() {
  const that = this;
  return function style(properties, zoom) {
    const borderStyle = zoom > 6 ? that.defaultFeatureBorderStyle : that.noBorderStyle;
    return {
      ...that.defaultFeatureStyle,
      ...borderStyle,
      fillColor: that.fillColor(that.calculateValueOfInterest(properties)),
    };
  };
}

function createVectorGrid(geoJson) {
  const vg = L.vectorGrid.slicer(
    geoJson,
    {
      noWrap: true,
      rendererFactory: L.canvas.tile,
      interactive: true,
      getFeatureId(feature) {
        return feature.properties.id;
      },
      vectorTileLayerStyles: {
        sliced: this.vectorTileLayerStyleFunction,
      },
      maxZoom: 24,
      tolerance: 2,
      bounds: [[-90,-180], [90,180]],
    },
  ).on({
    mouseover: this.handleVectorTileMouseover,
    mouseout: this.handleVectorTileMouseout,
    click: this.handleVectorTileClick,
  });
  return vg;
}

function clearHighlight(key) {
  if (this.highlights[key]) {
    this.vectorGrid.resetFeatureStyle(this.highlights[key]);
  }
  this.highlights[key] = null;
}

function clearMouseover() {
  if (this.highlights.mouseOver !== this.highlights.clicked) {
    this.clearHighlight('mouseOver');
  }
}

function highlightVectorGridFeature(properties, weight, fillOpacity, color) {
  if (this.highlights.clicked !== properties.id) {
    this.vectorGrid.setFeatureStyle(
      properties.id,
      {
        weight: weight || 1,
        fillOpacity: fillOpacity || 0.7,
        color: color || 'white',
        fill: true,
        stroke: true,
        fillColor: this.fillColor(this.calculateValueOfInterest(properties)),
      },
    );
  }
}

function handleVectorTileMouseover(event) {
  const { properties } = event.layer;
  this.clearMouseover();
  this.highlightVectorGridFeature(properties);
  this.highlights.mouseOver = properties.id;
  L.popup({
    className: 'custom-popup',
    autoPan: false,
    closeButton: false,
  }).setLatLng(event.latlng)
    .setContent(properties.name)
    .openOn(this.map);
}

function handleVectorTileMouseout(event) {
  this.clearMouseover();
}

function handleVectorTileClick(event) {
  const { properties } = event.layer;
  if (properties.id !== this.highlights.clicked) {
    this.clearClicked();
    this.highlightVectorGridFeature(properties, 3, 0.9);
    this.highlights.clicked = properties.id;
    this.selectedZone = properties;
    EventBus.$emit('clickCountry');
  } else {
    this.clearClicked();
  }
}

function clearClicked() {
  this.clearHighlight('clicked');
}

function findCorners(geoJson) {
  const stack = geoJson.features.map(feature => feature.geometry.coordinates);
  const limits = {
    top: Number.NEGATIVE_INFINITY,
    right: Number.NEGATIVE_INFINITY,
    bottom: Number.POSITIVE_INFINITY,
    left: Number.POSITIVE_INFINITY,
  };
  let current = {};
  console.log('stack size', stack.length);
  while (stack.length > 0) {
    current = stack.pop();
    if (Array.isArray(current[0])) {
      stack.push(...current);
    } else {
      const [lat, lng] = current;
      if (lat < limits.bottom) {
        limits.bottom = lat;
      } else {
        limits.top = Math.max(limits.top, lat);
      }
      if (lng < limits.left) {
        limits.left = lng;
      } else {
        limits.right = Math.max(limits.right, lng);
      }
    }
  }
  console.log('limits', limits);
  this.map.fitBounds([
    [limits.left, limits.top],
    [limits.right, limits.bottom],
  ]);
}

function loadWithData(geojson) {
  this.geoJson = geojson;
  EventBus.$emit('geojsonLoad', geojson);
  this.loadingModal.message = 'Finding corners';
  this.findCorners(geojson);
  this.loadingModal.message = 'Building scale'; // longName, key
  this.variable = this.metadata.attributes.find(
    attr => attr.id == this.metadata.initial_viz_value
  );
  this.buildScale();
  this.loadingModal.message = 'Creating vector grid';
  this.vectorGrid = this.createVectorGrid(geojson);
  this.vectorGrid.on('load', this.hideLoading);
  this.loadingModal.message = 'Loading options';
  EventBus.$emit('loaded', this.metadata);
  this.loadingModal.message = 'Adding to map';
  this.vectorGrid.addTo(this.map);
}

async function initVectorTiles() {
  this.resultDownload.current = 0;
  this.loading = true;
  this.loadingModal.message = 'Downloading results';
  let results = {};
  const axiosConfig = { onDownloadProgress: this.updateProgress };
  if (this.selectedLevel) {
    results = await axios.get(`${this.serviceBaseURL}/${this.selectedLevel}`, axiosConfig);
  } else {
    results = await axios.get(`${this.serviceBaseURL}`, axiosConfig);
    this.selectedLevel = results.data.schema.metadata.scale;
  }
  this.metadata = results.data.schema.metadata;
  this.loadWithData(results.data);
}

function hideLoading() {
  this.loading = false;
}

function fillColor(value) {
  // console.log('value', value);
  const result = this.colorScale(value);
  // console.log('result', result);
  return result;
}

function calculateValueOfInterest(properties) {
  return properties[this.variable.id];
}

function updateProgress(progressEvent) {
  this.resultDownload.max = progressEvent.total;
  this.resultDownload.current = progressEvent.loaded;
}

function setupEvents() {
  const that = this;
  EventBus.$on('changeColor', (newColor) => {
    that.clearClicked();
    that.colorScheme = newColor;
    that.buildScale();
    that.vectorGrid.redraw();
  });
  EventBus.$on('changeVariable', (newVariable) => {
    that.variable = that.metadata.attributes.find(
      attr => attr.id == newVariable.id
    );
    that.buildScale();
    that.vectorGrid.redraw();
  });
  EventBus.$on('changeNColorCategories', (newN) => {
    that.nColorCategories = newN;
    that.buildScale();
    that.vectorGrid.redraw();
  });
  EventBus.$on('changeScaleType', (newScaleType) => {
    that.changeScaleType(newScaleType);
    that.vectorGrid.redraw();
  });
}

function detailPanelContent() {
  if (!this.selectedZone) return null;
  return {
    variable: this.variable.id,
    number: this.calculateValueOfInterest(this.selectedZone),
    ...this.selectedZone,
  };
}

export default {
  name: 'Map',
  components: {
    LoadingModal,
    MapLegend,
    DetailPanel,
  },
  data() {
    return {
      serviceBaseURL: `/api/${this.$route.params.projectid}`,
      selectedLevel: this.$route.params.level,
      metadata: { },
      map: null,
      tileLayer: null,
      vectorGrid: null,
      colorScheme: 'purple-red',
      colorScale: null, // Will be overridden when map loads
      nColorCategories: 4,
      variable: {
        key: null,
        longName: null,
      },
      defaultFeatureBorderStyle: {
        weight: 1,
        opacity: 1,
        color: 'white',
      },
      scales: {
        selected: 'natural_breaks',
        options: {
          natural_breaks: scaleCluster,
          quantile: d3Scale.scaleQuantile
        }
      },
      noBorderStyle: {
        weight: 0.5,
        opacity: 0.8,
        color: 'grey',
      },
      defaultFeatureStyle: {
        fill: true,
        fillOpacity: 0.7,
      },
      loading: false,
      loadingModal: {
        message: 'Loading',
      },
      selectedZone: null,
      highlights: {
        mouseOver: null,
        clicked: null,
      },
      resultDownload: {
        current: 0,
        max: 100,
      }
    };
  },
  mounted() {
    this.initMap();
    this.initVectorTiles();
    this.setupEvents();
  },
  methods: {
    initMap,
    initVectorTiles,
    createVectorGrid,
    fillColor,
    clearMouseover,
    clearClicked,
    highlightVectorGridFeature,
    handleVectorTileMouseover,
    handleVectorTileMouseout,
    handleVectorTileClick,
    hideLoading,
    buildScale,
    calculateValueOfInterest,
    setupEvents,
    clearHighlight,
    loadWithData,
    findCorners,
    updateProgress,
    changeScaleType,
    getColorRange,
  },
  computed: {
    styleFunction,
    vectorTileLayerStyleFunction,
    detailPanelContent,
  },
  watch: {
    $route(to) {
      this.selectedLevel = to.params.level;
      this.vectorGrid.removeFrom(this.map);
      this.vectorGrid = null;
      this.initVectorTiles();
    },
  },
};
</script>
